import { useState } from "react";

import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  RefreshCw,
  X,
} from "lucide-react";

import type { BookingApprovalResponse } from "@/api/generated/types.gen";
import { DataTablePagination } from "@/components/DataTables/DataTablePagination";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ModalCanvas, ModalSection } from "@/components/ui/modal-section";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useApproveBookingApproval,
  useRejectBookingApproval,
  useTriggerBookingSync,
} from "@/hooks/mutations/use-booking-approvals";
import {
  type BookingApprovalsFilters,
  useBookingApprovals,
} from "@/hooks/queries/use-booking-approvals";
import { toTitleCase } from "@/utils/string";

import { FilterBar } from "./components/FilterBar";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const STATUS_FILTERS = ["PENDING", "APPROVED", "REJECTED", "ALL"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const STATUS_BADGE_VARIANT: Record<string, "success" | "warning" | "destructive"> = {
  APPROVED: "success",
  PENDING: "warning",
  REJECTED: "destructive",
};

const fmtDate = (v?: string | null): string =>
  v ? new Date(v).toLocaleString("en-AU", { dateStyle: "medium" }) : "—";

const formatBytes = (bytes?: number | null): string => {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs uppercase tracking-wide text-foreground-muted">
      {label}
    </span>
    <span className="text-sm">{value?.toString().trim() || "—"}</span>
  </div>
);

const MatchBadge = ({ matchStatus }: { matchStatus: string }) =>
  matchStatus === "UNMATCHED" ? (
    <Badge variant="destructive">Unmatched</Badge>
  ) : (
    <Badge variant="outline">Matched</Badge>
  );

const BookingRow = ({
  approval,
  onOpen,
}: {
  approval: BookingApprovalResponse;
  onOpen: () => void;
}) => {
  const approve = useApproveBookingApproval();
  const reject = useRejectBookingApproval();
  const payload = approval.payload;
  const isBusy = approve.isPending || reject.isPending;

  return (
    <TableRow className="cursor-pointer hover:bg-[#E8FDFF]/50" onClick={onOpen}>
      <TableCell className="font-medium">
        {payload?.case_details?.case_number ?? approval.vinci_booking_id}
      </TableCell>
      <TableCell>{payload?.medical_examiner?.name || "—"}</TableCell>
      <TableCell>{payload?.referrer?.organisation || "—"}</TableCell>
      <TableCell>{payload?.examinee?.name || "—"}</TableCell>
      <TableCell>{fmtDate(payload?.case_details?.appointment_date)}</TableCell>
      <TableCell>
        {approval.match_status === "UNMATCHED" ? (
          <Badge variant="destructive">Unmatched</Badge>
        ) : (
          <span className="text-foreground-muted">Matched</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_BADGE_VARIANT[approval.status] ?? "secondary"}>
          {toTitleCase(approval.status)}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant="simple-outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              approve.mutate(approval.id);
            }}
            disabled={isBusy}
          >
            <Check className="size-4" />
            Approve
          </Button>
          <Button
            variant="simple-outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              const who = payload?.examinee?.name || approval.vinci_booking_id;
              if (window.confirm(`Reject the booking for ${who}?`)) {
                reject.mutate({ id: approval.id });
              }
            }}
            disabled={isBusy}
          >
            <X className="size-4" />
            Reject
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const BookingApprovalsPage = () => {
  const [filter, setFilter] = useState<StatusFilter>("PENDING");
  const [appliedFilters, setAppliedFilters] = useState<BookingApprovalsFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data, isLoading } = useBookingApprovals({
    status: filter === "ALL" ? undefined : filter,
    filters: appliedFilters,
    page,
    limit: pageSize,
  });
  const approvals = data?.items ?? [];
  const totalPages = Math.max(1, data?.total_pages ?? 1);

  const onFilterChange = (next: StatusFilter) => {
    setFilter(next);
    setPage(1);
  };

  const onFiltersBarChange = (filters: BookingApprovalsFilters) => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const hasActiveFilters = Object.values(appliedFilters).some(
    (value) => value !== undefined
  );

  const approveDialog = useApproveBookingApproval();
  const rejectDialog = useRejectBookingApproval();
  const sync = useTriggerBookingSync();

  const selected = selectedIndex != null ? approvals[selectedIndex] : undefined;
  const payload = selected?.payload;
  const isPending = selected?.status === "PENDING";
  const approveError =
    approveDialog.error instanceof Error ? approveDialog.error.message : null;

  const goTo = (index: number) => {
    approveDialog.reset();
    rejectDialog.reset();
    setRejectReason("");
    setSelectedIndex(index);
  };

  const closeDialog = () => {
    approveDialog.reset();
    rejectDialog.reset();
    setRejectReason("");
    setSelectedIndex(null);
  };

  const onApprove = async () => {
    if (!selected) return;
    await approveDialog
      .mutateAsync(selected.id)
      .then(closeDialog)
      .catch(() => {});
  };

  const onReject = async () => {
    if (!selected) return;
    await rejectDialog
      .mutateAsync({ id: selected.id, reason: rejectReason || undefined })
      .then(closeDialog)
      .catch(() => {});
  };

  const currentIndex = selectedIndex ?? 0;

  return (
    <div className="space-y-2 p-2 pl-0">
      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4">
        <PageHeader
          title="Booking Approvals"
          description="Review directory bookings and approve them into Report Studio cases."
          rightContent={
            <Button
              size="sm"
              className="shadow-[0_6px_16px_rgba(26,25,57,0.22)]"
              onClick={() => sync.mutate()}
              isLoading={sync.isPending}
            >
              <RefreshCw className="size-4" />
              Sync now
            </Button>
          }
        />
      </div>

      <div className="flex gap-2">
        {STATUS_FILTERS.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={filter === s ? "default" : "simple-outline"}
            onClick={() => onFilterChange(s)}
          >
            {toTitleCase(s)}
          </Button>
        ))}
      </div>

      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold tracking-[-0.01em]">Bookings</h2>
            <Badge variant="secondary">{data?.total ?? 0}</Badge>
          </div>
          <FilterBar onFiltersChange={onFiltersBarChange} />
        </div>

        <Table className="border-t border-foreground-muted/20">
          <TableHeader>
            <TableRow>
              <TableHead>Booking</TableHead>
              <TableHead>Medical Examiner</TableHead>
              <TableHead>Referrer</TableHead>
              <TableHead>Examinee</TableHead>
              <TableHead>Appointment</TableHead>
              <TableHead>Match</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-foreground-muted"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : approvals.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-foreground-muted"
                >
                  No booking approvals{hasActiveFilters ? " for that filter" : ""}.
                </TableCell>
              </TableRow>
            ) : (
              approvals.map((a, i) => (
                <BookingRow key={a.id} approval={a} onOpen={() => goTo(i)} />
              ))
            )}
          </TableBody>
        </Table>

        {approvals.length > 0 && (
          <DataTablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={data?.total}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            showSelectedCount={false}
          />
        )}
      </div>

      <Dialog open={selected != null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {selected && payload && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3 pr-6">
                  <div className="flex items-center gap-2">
                    <DialogTitle>
                      Booking{" "}
                      {payload.case_details?.case_number ??
                        selected.vinci_booking_id}
                    </DialogTitle>
                    <Badge
                      variant={STATUS_BADGE_VARIANT[selected.status] ?? "secondary"}
                    >
                      {toTitleCase(selected.status)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="simple-outline"
                      size="icon"
                      className="size-8"
                      aria-label="Previous booking"
                      onClick={() => goTo(Math.max(0, currentIndex - 1))}
                      disabled={currentIndex === 0}
                    >
                      <ChevronLeft className="size-4" />
                    </Button>
                    <span className="whitespace-nowrap text-sm text-foreground-muted">
                      {currentIndex + 1} of {approvals.length}
                    </span>
                    <Button
                      variant="simple-outline"
                      size="icon"
                      className="size-8"
                      aria-label="Next booking"
                      onClick={() =>
                        goTo(Math.min(approvals.length - 1, currentIndex + 1))
                      }
                      disabled={currentIndex >= approvals.length - 1}
                    >
                      <ChevronRight className="size-4" />
                    </Button>
                  </div>
                </div>
                <DialogDescription className="text-sm text-foreground-muted">
                  {[
                    payload.examinee?.name,
                    payload.case_details?.service_type,
                    fmtDate(payload.case_details?.appointment_date),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap items-center gap-2">
                <MatchBadge matchStatus={selected.match_status} />
                {payload.case_details?.urgent_report && (
                  <Badge variant="outline">Urgent report</Badge>
                )}
                {payload.case_details?.telehealth && (
                  <Badge variant="outline">Telehealth</Badge>
                )}
                {payload.case_details?.is_secure_and_switch && (
                  <Badge variant="outline">Secure&Switch</Badge>
                )}
              </div>

              <ModalCanvas>
                <ModalSection
                  title="Medical examiner"
                  description="Who will conduct the assessment"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Name" value={payload.medical_examiner?.name} />
                    <Field label="Email" value={payload.medical_examiner?.email} />
                  </div>
                  <Field
                    label="Specialty"
                    value={payload.medical_examiner?.specialty}
                  />
                </ModalSection>

                <ModalSection
                  title="Referral"
                  description="Who requested the assessment"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Referrer" value={payload.referrer?.name} />
                    <Field
                      label="Organisation"
                      value={payload.referrer?.organisation}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field
                      label="Case Type"
                      value={payload.case_details?.case_type}
                    />
                    <Field
                      label="Service Type"
                      value={payload.case_details?.service_type}
                    />
                  </div>
                </ModalSection>

                <ModalSection
                  title="Assessment details"
                  description="Schedule and requirements"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <Field
                      label="Claim Number"
                      value={payload.case_details?.claim_number}
                    />
                    <Field label="Location" value={payload.case_details?.location} />
                    <Field
                      label="Booking Date"
                      value={fmtDate(payload.case_details?.booking_date)}
                    />
                    <Field
                      label="Date of Injury"
                      value={fmtDate(payload.case_details?.date_of_injury)}
                    />
                    <Field label="Employer" value={payload.examinee?.employer} />
                    <Field
                      label="Interpreter"
                      value={
                        payload.case_details?.interpreter_required
                          ? `Yes${
                              payload.case_details?.interpreter_language
                                ? ` (${payload.case_details.interpreter_language})`
                                : ""
                            }`
                          : "No"
                      }
                    />
                  </div>
                </ModalSection>

                <ModalSection
                  title="Documents"
                  description={`${payload.documents?.length ?? 0} file${
                    payload.documents?.length === 1 ? "" : "s"
                  }`}
                >
                  {payload.documents && payload.documents.length > 0 ? (
                    <ul className="divide-y divide-border rounded-md border">
                      {payload.documents.map((doc) => (
                        <li
                          key={
                            doc.document_id ?? `${doc.file_name}-${doc.date_added}`
                          }
                          className="flex flex-col gap-1 px-3 py-2 text-sm"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <FileText className="h-4 w-4 shrink-0 text-foreground-muted" />
                            <span className="truncate">
                              {doc.file_name || "Untitled"}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 pl-6 text-xs text-foreground-muted">
                            {doc.file_ext && (
                              <Badge variant="outline" className="uppercase">
                                {doc.file_ext.replace(/^\./, "")}
                              </Badge>
                            )}
                            {doc.bytes != null && (
                              <span>{formatBytes(doc.bytes)}</span>
                            )}
                            <span>{fmtDate(doc.date_added)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-foreground-muted">No documents.</p>
                  )}
                </ModalSection>

                {isPending && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Reason for rejection (optional)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                    {approveError && (
                      <p className="text-sm text-destructive">{approveError}</p>
                    )}
                  </div>
                )}
              </ModalCanvas>

              <DialogFooter>
                {isPending ? (
                  <>
                    <Button
                      variant="ghost"
                      onClick={onReject}
                      disabled={rejectDialog.isPending || approveDialog.isPending}
                      isLoading={rejectDialog.isPending}
                    >
                      Reject
                    </Button>
                    <Button
                      onClick={onApprove}
                      disabled={approveDialog.isPending || rejectDialog.isPending}
                      isLoading={approveDialog.isPending}
                    >
                      Approve booking
                    </Button>
                  </>
                ) : (
                  <Button variant="simple-outline" onClick={closeDialog}>
                    Close
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingApprovalsPage;
